import { useState } from 'react';
import { Box, Chip, Paper, Skeleton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { ExpandMore as ExpandMoreIcon, KeyboardArrowUp as KeyboardArrowUpIcon } from '@mui/icons-material';
import { STableLoader } from '../../../shared_elements';
import NumericLabel from 'react-pretty-numbers';

const InvoiceRow = ({ item, currency }) => {
    const [expanded, setExpanded] = useState(false);
    const hasItems = item?.invoice_items?.length > 0;

    return (
        <>
            <TableRow className="table-row">
                <TableCell
                    className="table-cell"
                    sx={{ width: 32, cursor: hasItems ? 'pointer' : 'default' }}
                    onClick={() => hasItems && setExpanded(p => !p)}
                >
                    {hasItems
                        ? expanded
                            ? <Tooltip title="Close Item" arrow><KeyboardArrowUpIcon sx={{ verticalAlign: 'middle' }} fontSize="small" /></Tooltip>
                            : <Tooltip title="View Item" arrow><ExpandMoreIcon sx={{ verticalAlign: 'middle' }} fontSize="small" /></Tooltip>
                        : null}
                </TableCell>
                <TableCell className="table-cell">
                    <Typography variant="caption" sx={{ fontSize: '12px', fontWeight: 500 }}>
                        {item?.asset?.name || '--'}
                    </Typography>
                    {item?.invoice_number && (
                        <Tooltip title="View Invoice" arrow>
                            <div
                                style={{ fontSize: '11px', color: '#1976d2', cursor: 'pointer', textDecoration: 'underline' }}
                                onClick={() => window.open(`/invoices/view/${item?.slug}`, '_blank')}
                            >
                                {item.invoice_number}
                            </div>
                        </Tooltip>
                    )}
                </TableCell>
                <TableCell className="table-cell" sx={{ wordBreak: 'break-word', maxWidth: '220px' }}>{item?.description_line || '--'}</TableCell>
                <TableCell className="table-cell"></TableCell>
                <TableCell className="table-cell"></TableCell>
                <TableCell className="table-cell"></TableCell>
                <TableCell className="table-cell"></TableCell>
                <TableCell className="table-cell" align="right">
                    <NumericLabel params={{ locales: 'en-GB', currency: true, currencyIndicator: currency, precision: 2, commafy: true, justification: 'L' }}>
                        {item?.total_amount || 0}
                    </NumericLabel>
                </TableCell>
            </TableRow>
            {expanded && item?.invoice_items?.map((subItem, idx) => (
                <TableRow key={idx} sx={{ backgroundColor: '#f5f8ff' }}>
                    <TableCell />
                    <TableCell className="table-cell">
                        <Typography variant="caption" sx={{ fontSize: '12px', fontWeight: 500 }}>
                            {subItem?.invoice_item_name || '--'}
                        </Typography>
                    </TableCell>
                    <TableCell className="table-cell" sx={{ wordBreak: 'break-word', maxWidth: '220px' }}>
                        <Typography variant="caption" sx={{ fontSize: '12px' }}>{subItem?.description || '--'}</Typography>
                    </TableCell>
                    <TableCell className="table-cell">
                        <Typography variant="caption" sx={{ fontSize: '12px' }}>
                            <NumericLabel params={{ locales: 'en-GB', currency: true, currencyIndicator: currency, precision: 2, commafy: true, justification: 'L' }}>
                                {subItem?.rate_per_unit || 0}
                            </NumericLabel>
                        </Typography>
                    </TableCell>
                    <TableCell className="table-cell">
                        <NumericLabel params={{ locales: 'en-GB', currency: true, currencyIndicator: currency, precision: 2, commafy: true, justification: 'L' }}>
                            {subItem?.sub_total || 0}
                        </NumericLabel>
                    </TableCell>
                    <TableCell className="table-cell">
                           <span style={{ fontSize: '12px' }}>{subItem.tax_rate}%</span>     
                    </TableCell>
                    <TableCell className="table-cell">
                            <span style={{ fontSize: '12px' }}>{subItem.tax_amount}</span>    
                    </TableCell>
                    <TableCell className="table-cell" align="right">
                        <NumericLabel params={{ locales: 'en-GB', currency: true, currencyIndicator: currency, precision: 2, commafy: true, justification: 'L' }}>
                            {subItem?.sub_amount_total || 0}
                        </NumericLabel>
                    </TableCell>
                </TableRow>
            ))}
        </>
    );
};

const SummaryInvoiceItem = ({ skeletonLoader, invoice }) => {
    const currency = invoice?.currency;

    return (
        <Box className="items-section">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '5px', paddingRight: '10px' }}>
                <Typography variant="subtitle1" className="party-heading">
                    INVOICE ITEM'S
                </Typography>
            </div>
            <TableContainer component={Paper} className="invoice-table lease-detail-table">
                {skeletonLoader ? <STableLoader count={6} rowCount={3} /> :
                    <Table className='mui-table-format' stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell className="table-header" sx={{ width: 32 }} />
                                <TableCell className="table-header" sx={{ minWidth: '140px' }}>Item</TableCell>
                                <TableCell className="table-header" sx={{ minWidth: '140px' }}>Description</TableCell>
                                <TableCell className="table-header" sx={{ minWidth: '120px' }}>Rate/Unit</TableCell>
                                <TableCell className="table-header" sx={{ minWidth: '100px' }}>Amount</TableCell>
                                <TableCell className="table-header" sx={{ minWidth: '80px' }}>Tax Rate</TableCell>
                                <TableCell className="table-header" sx={{ minWidth: '80px' }}>Tax Amount</TableCell>
                                <TableCell className="table-header" align="right" sx={{ minWidth: '100px' }}>Sub Total</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {invoice?.invoices?.length > 0
                                ? [...invoice.invoices].sort((a, b) => (a.asset?.asset_type ?? 0) - (b.asset?.asset_type ?? 0)).map((item, index) => (
                                    <InvoiceRow key={index} item={item} currency={currency} />
                                ))
                                : <TableRow>
                                    <TableCell colSpan={7} align="center">No Records Found</TableCell>
                                  </TableRow>
                            }
                        </TableBody>
                    </Table>
                }
            </TableContainer>
            <Stack style={{ lineHeight: '8px' }} spacing={0}>
                <div style={{ textAlign: 'end' }}>
                    {skeletonLoader ? (
                        <>
                            <Skeleton variant="text" width={200} height={22} sx={{ ml: 'auto' }} />
                            {/* <Skeleton variant="text" width={180} height={22} sx={{ ml: 'auto' }} /> */}
                            <Skeleton variant="text" width={190} height={22} sx={{ ml: 'auto' }} />
                        </>
                    ) : (
                        <>
                            <p className="invoice-balance-summary">Total Amount: <span><NumericLabel params={{ locales: 'en-GB', currency: true, currencyIndicator: currency, precision: 2, commafy: true, justification: 'L' }}>{invoice?.billing_summary?.billable_amount || '0.00'}</NumericLabel></span></p>
                            {invoice?.status?.value ===5 &&<p className="invoice-balance-summary">Paid Amount: <span><NumericLabel params={{ locales: 'en-GB', currency: true, currencyIndicator: currency, commafy: true, precision: 2, justification: 'L' }}>{invoice?.partial_paid_amount}</NumericLabel></span></p>}
                            {invoice?.status?.value ===5 && <p className="invoice-balance-summary">Remaining Amount: <span><NumericLabel params={{ locales: 'en-GB', currency: true, currencyIndicator: currency, commafy: true, precision: 2, justification: 'L' }}>{invoice?.remaining_balance_amount || '0.00'}</NumericLabel></span></p>}
                        </>
                    )}
                </div>
            </Stack>
        </Box>
    );
};

export default SummaryInvoiceItem;
